import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import api from '../../utils/api';
import './AdminPages.css';
import './ThemeSettings.css';

const FONT_OPTIONS = [
  {
    name: 'Inter',
    value: 'Inter, sans-serif',
    weights: ['300', '400', '500', '600', '700']
  },
  {
    name: 'Montserrat',
    value: 'Montserrat, sans-serif',
    weights: ['300', '400', '500', '600', '700']
  },
  {
    name: 'Nunito',
    value: 'Nunito, sans-serif',
    weights: ['300', '400', '500', '600', '700']
  },
  {
    name: 'Lato',
    value: 'Lato, sans-serif',
    weights: ['300', '400', '700']
  },
  {
    name: 'Arial',
    value: 'Arial, sans-serif',
    weights: ['400', '700']
  },
  {
    name: 'Georgia',
    value: 'Georgia, serif',
    weights: ['400', '700']
  }
];

const FONT_WEIGHT_LABELS = {
  '300': 'Thin',
  '400': 'Normal',
  '500': 'Medium',
  '600': 'Semi Bold',
  '700': 'Bold'
};

const ThemeSettings = () => {
  const navigate = useNavigate();
  const { updateTheme } = useTheme();
  const [theme, setTheme] = useState({
    primary_color: '#4F46E5',
    secondary_color: '#10B981',
    font_family: 'Inter, sans-serif',
    font_weight: '400'
  });
  const [selectedFont, setSelectedFont] = useState(FONT_OPTIONS[0]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await api.get('/admin/theme');
        const themeData = response.data;
        setTheme(themeData);

        // Find matching font
        const fontMatch = FONT_OPTIONS.find(f => themeData.font_family?.includes(f.name));
        if (fontMatch) setSelectedFont(fontMatch);
      } catch (err) {
        console.error('Failed to fetch theme:', err);
      }
    };
    fetchTheme();
  }, []);

  const handleFontChange = (font) => {
    setSelectedFont(font);
    setTheme({ ...theme, font_family: font.value });
  };

  const handlePreview = () => {
    updateTheme(theme);
    setPreviewMode(true);
    setSuccess('Preview mode activated! This is how your site will look. Click "Publish" to make it live.');
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      await api.put('/admin/theme', theme);
      setSuccess('Theme saved as draft successfully!');
      setPreviewMode(false);
    } catch (err) {
      setError('Failed to save theme');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setLoading(true);
    setError('');
    try {
      await api.put('/admin/theme', theme);
      await api.post('/admin/theme/publish');
      setSuccess('Theme published successfully! Your changes are now live.');
      updateTheme(theme);
      setPreviewMode(false);
    } catch (err) {
      setError('Failed to publish theme');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header-modern">
          <div>
            <h1>🎨 Theme Customization</h1>
            <p className="page-subtitle">Customize your website's look and feel</p>
          </div>
          <div className="button-group">
            <button onClick={() => navigate('/admin')} className="btn btn-outline">
              ← Back
            </button>
            {previewMode && (
              <span className="preview-badge">Preview Mode Active</span>
            )}
          </div>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="theme-grid">
          {/* Color Settings */}
          <div className="theme-card">
            <div className="theme-card-header">
              <h3>🎨 Color Palette</h3>
              <p>Choose colors that represent your brand</p>
            </div>
            <div className="theme-card-body">
              {/* Primary Color */}
              <div className="color-picker-group">
                <label>Primary Color</label>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    value={theme.primary_color}
                    onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })}
                    className="color-input"
                  />
                  <div className="color-info">
                    <div
                      className="color-preview"
                      style={{ backgroundColor: theme.primary_color }}
                    />
                    <input
                      type="text"
                      value={theme.primary_color}
                      onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })}
                      className="color-hex-input"
                      placeholder="#000000"
                    />
                  </div>
                </div>
                <small>Used for buttons, links, and accents</small>
              </div>

              {/* Secondary Color */}
              <div className="color-picker-group">
                <label>Secondary Color</label>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    value={theme.secondary_color}
                    onChange={(e) => setTheme({ ...theme, secondary_color: e.target.value })}
                    className="color-input"
                  />
                  <div className="color-info">
                    <div
                      className="color-preview"
                      style={{ backgroundColor: theme.secondary_color }}
                    />
                    <input
                      type="text"
                      value={theme.secondary_color}
                      onChange={(e) => setTheme({ ...theme, secondary_color: e.target.value })}
                      className="color-hex-input"
                      placeholder="#000000"
                    />
                  </div>
                </div>
                <small>Used for secondary elements and highlights</small>
              </div>

              {/* Color Presets */}
              <div className="color-presets">
                <p style={{ fontWeight: '600', marginBottom: '10px' }}>Quick Presets:</p>
                <div className="preset-grid">
                  <button
                    onClick={() => setTheme({ ...theme, primary_color: '#4F46E5', secondary_color: '#10B981' })}
                    className="preset-btn"
                    style={{ background: 'linear-gradient(135deg, #4F46E5, #10B981)' }}
                    title="Modern Purple & Green"
                  />
                  <button
                    onClick={() => setTheme({ ...theme, primary_color: '#EF4444', secondary_color: '#F97316' })}
                    className="preset-btn"
                    style={{ background: 'linear-gradient(135deg, #EF4444, #F97316)' }}
                    title="Bold Red & Orange"
                  />
                  <button
                    onClick={() => setTheme({ ...theme, primary_color: '#3B82F6', secondary_color: '#06B6D4' })}
                    className="preset-btn"
                    style={{ background: 'linear-gradient(135deg, #3B82F6, #06B6D4)' }}
                    title="Ocean Blue & Cyan"
                  />
                  <button
                    onClick={() => setTheme({ ...theme, primary_color: '#8B5CF6', secondary_color: '#EC4899' })}
                    className="preset-btn"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}
                    title="Purple & Pink"
                  />
                  <button
                    onClick={() => setTheme({ ...theme, primary_color: '#059669', secondary_color: '#84CC16' })}
                    className="preset-btn"
                    style={{ background: 'linear-gradient(135deg, #059669, #84CC16)' }}
                    title="Forest Green"
                  />
                  <button
                    onClick={() => setTheme({ ...theme, primary_color: '#1F2937', secondary_color: '#6B7280' })}
                    className="preset-btn"
                    style={{ background: 'linear-gradient(135deg, #1F2937, #6B7280)' }}
                    title="Professional Gray"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Typography Settings */}
          <div className="theme-card">
            <div className="theme-card-header">
              <h3>✍️ Typography</h3>
              <p>Select fonts and weights for your content</p>
            </div>
            <div className="theme-card-body">
              {/* Font Family */}
              <div className="form-group">
                <label>Font Family</label>
                <div className="font-grid">
                  {FONT_OPTIONS.map((font) => (
                    <button
                      key={font.value}
                      onClick={() => handleFontChange(font)}
                      className={`font-option ${selectedFont.value === font.value ? 'active' : ''}`}
                      style={{ fontFamily: font.value }}
                    >
                      {font.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Weight */}
              <div className="form-group">
                <label>Font Weight</label>
                <div className="weight-grid">
                  {selectedFont.weights.map((weight) => (
                    <button
                      key={weight}
                      onClick={() => setTheme({ ...theme, font_weight: weight })}
                      className={`weight-option ${theme.font_weight === weight ? 'active' : ''}`}
                      style={{ fontFamily: selectedFont.value, fontWeight: weight }}
                    >
                      <span className="weight-label">{FONT_WEIGHT_LABELS[weight]}</span>
                      <span className="weight-value">{weight}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview Text */}
              <div className="font-preview">
                <p style={{
                  fontFamily: theme.font_family,
                  fontWeight: theme.font_weight || '400',
                  fontSize: '24px',
                  marginBottom: '10px'
                }}>
                  The quick brown fox jumps over the lazy dog
                </p>
                <p style={{
                  fontFamily: theme.font_family,
                  fontWeight: theme.font_weight || '400',
                  fontSize: '16px',
                  color: '#666'
                }}>
                  ABCDEFGHIJKLMNOPQRSTUVWXYZ<br />
                  abcdefghijklmnopqrstuvwxyz<br />
                  0123456789
                </p>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="theme-card preview-card">
            <div className="theme-card-header">
              <h3>👁️ Live Preview</h3>
              <p>See how your theme looks</p>
            </div>
            <div className="theme-card-body">
              <div className="preview-elements">
                <button
                  className="preview-button"
                  style={{
                    backgroundColor: theme.primary_color,
                    fontFamily: theme.font_family,
                    fontWeight: theme.font_weight
                  }}
                >
                  Primary Button
                </button>
                <button
                  className="preview-button"
                  style={{
                    backgroundColor: theme.secondary_color,
                    fontFamily: theme.font_family,
                    fontWeight: theme.font_weight
                  }}
                >
                  Secondary Button
                </button>
                <h1 style={{
                  color: theme.primary_color,
                  fontFamily: theme.font_family,
                  fontWeight: theme.font_weight
                }}>
                  Heading Text
                </h1>
                <p style={{
                  fontFamily: theme.font_family,
                  fontWeight: theme.font_weight
                }}>
                  This is body text using your selected font and weight.
                </p>
                <a href="#" style={{
                  color: theme.primary_color,
                  fontFamily: theme.font_family,
                  fontWeight: theme.font_weight
                }}>
                  Sample Link
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="theme-actions">
          <button
            onClick={handlePreview}
            className="btn btn-secondary btn-lg"
            disabled={loading}
          >
            👁️ Preview Changes
          </button>
          <button
            onClick={handleSave}
            className="btn btn-outline btn-lg"
            disabled={loading}
          >
            💾 Save as Draft
          </button>
          <button
            onClick={handlePublish}
            className="btn btn-primary btn-lg"
            disabled={loading}
          >
            {loading ? '⏳ Publishing...' : '🚀 Publish Theme'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeSettings;
